import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  ArrowRight, 
  Globe, 
  Wallet, 
  TrendingUp, 
  Shield, 
  Zap,
  Building2,
  Users,
  ArrowLeftRight,
  PiggyBank
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3f59e367-5274-48ad-9fe7-f7e5ca8c3072/artifacts/mug85kt6_onepay%20logo%20best.jpg";

const features = [
  {
    icon: Globe,
    title: "Cross-Border Payments",
    description: "Send money instantly across Africa. KES, UGX, TZS, RWF & USD supported."
  },
  {
    icon: ArrowLeftRight,
    title: "Instant FX",
    description: "Convert currencies in seconds with transparent rates. No hidden fees."
  },
  {
    icon: PiggyBank,
    title: "Savings Vaults",
    description: "Lock away your savings and watch them grow. Flexible or fixed terms."
  },
  {
    icon: TrendingUp,
    title: "Liquidity Advances",
    description: "Get quick access to funds when you need them. No collateral required."
  },
  {
    icon: Building2,
    title: "SME Solutions",
    description: "Working capital and supplier payments for growing businesses."
  },
  {
    icon: Shield,
    title: "Secure & Trusted",
    description: "Bank-grade security with real-time transaction monitoring."
  }
];

const stats = [
  { value: "4", label: "Currencies" },
  { value: "<5s", label: "FX Settlement" },
  { value: "0.5%", label: "Lowest Spread" },
  { value: "24/7", label: "Availability" }
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="OnePay" className="h-8 w-auto" />
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Pricing
              </a>
              <a href="#business" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
                Business
              </a>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-sm font-medium" data-testid="nav-login-btn">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488] text-sm font-medium" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <Zap className="h-4 w-4 text-[#14B8A6]" />
                <span className="text-sm font-medium text-[#14B8A6]">Africa's Cross-Border Payment Solution</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
                Move Money Across Africa,{" "}
                <span className="text-[#14B8A6]">Instantly</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
                One wallet, multiple currencies. Send, receive, convert and save across Kenya, Uganda, Tanzania, and Rwanda with transparent pricing.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button size="lg" className="rounded-full bg-[#14B8A6] hover:bg-[#0D9488] text-base px-8 h-12 w-full sm:w-auto" data-testid="hero-get-started-btn">
                    Start Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline" className="rounded-full text-base px-8 h-12 w-full sm:w-auto border-2" data-testid="hero-learn-more-btn">
                    Learn More
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-6 pt-8 border-t border-slate-200">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                    <div className="text-xs text-slate-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Visual */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-3xl blur-3xl"></div>
              <Card className="relative bg-white shadow-2xl rounded-3xl overflow-hidden border-0">
                <CardContent className="p-0">
                  <div className="wallet-card-gradient p-6 text-white">
                    <p className="text-sm opacity-80">Total Balance</p>
                    <h2 className="text-4xl font-bold mt-2">$12,450.36</h2>
                    <div className="flex gap-3 mt-6">
                      <Button size="sm" className="rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm">
                        <Wallet className="h-4 w-4 mr-2" /> Deposit
                      </Button>
                      <Button size="sm" className="rounded-full bg-white hover:bg-slate-100 text-slate-900">
                        <ArrowRight className="h-4 w-4 mr-2" /> Transfer
                      </Button>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    <h3 className="font-semibold text-slate-900">Currency Balances</h3>
                    <div className="space-y-3">
                      {[
                        { currency: "USD", amount: "5,230.00", flag: "🇺🇸" },
                        { currency: "KES", amount: "425,000", flag: "🇰🇪" },
                        { currency: "UGX", amount: "2,500,000", flag: "🇺🇬" },
                        { currency: "TZS", amount: "1,200,000", flag: "🇹🇿" }
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{item.flag}</span>
                            <span className="font-medium text-slate-900">{item.currency}</span>
                          </div>
                          <span className="font-semibold text-slate-900">{item.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need for Cross-Border Finance
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whether you&apos;re sending money home or running a business, OnePay has the tools to move your money efficiently.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg transition-shadow duration-300 border-slate-200">
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4 group-hover:bg-[#14B8A6] transition-colors duration-300">
                    <feature.icon className="h-6 w-6 text-[#14B8A6] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Business Section */}
      <section id="business" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full">
                <Building2 className="h-4 w-4" />
                <span className="text-sm font-medium">For Business</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold">
                Power Your Business with Cross-Border Payments
              </h2>
              <p className="text-lg text-slate-300">
                Pay suppliers in Uganda, receive payments from Tanzania, manage working capital — all from one platform designed for African SMEs.
              </p>
              <ul className="space-y-4">
                {[
                  "Cross-border supplier payments",
                  "Working capital up to $500",
                  "Transaction tagging & reports",
                  "API access for integrations"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="h-6 w-6 rounded-full bg-[#10B981] flex items-center justify-center">
                      <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button size="lg" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 mt-4" data-testid="business-get-started-btn">
                  Start as SME <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            
            {/* SME Dashboard Mockup */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                {/* Mock Header */}
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="bg-slate-200 rounded-full px-4 py-1 text-xs text-slate-500">onepay.com/business</div>
                  </div>
                </div>
                {/* Mock Dashboard Content */}
                <div className="p-6 space-y-4">
                  {/* Balance Card */}
                  <div className="bg-gradient-to-r from-[#14B8A6] to-[#0D9488] rounded-xl p-4 text-white">
                    <p className="text-sm opacity-80">Business Balance</p>
                    <p className="text-2xl font-bold mt-1">$24,580.00</p>
                    <div className="flex gap-2 mt-3">
                      <span className="px-2 py-1 bg-white/20 rounded-full text-xs">+ Pay Supplier</span>
                      <span className="px-2 py-1 bg-white/20 rounded-full text-xs">+ Invoice</span>
                    </div>
                  </div>
                  {/* Recent Payments */}
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-slate-800">Recent Supplier Payments</p>
                    {[
                      { name: "Kampala Textiles Ltd", amount: "UGX 2,500,000", status: "Completed", flag: "🇺🇬" },
                      { name: "Dar es Salaam Imports", amount: "TZS 1,800,000", status: "Pending", flag: "🇹🇿" },
                      { name: "Kigali Electronics", amount: "RWF 950,000", status: "Completed", flag: "🇷🇼" },
                    ].map((payment, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{payment.flag}</span>
                          <div>
                            <p className="text-sm font-medium text-slate-800">{payment.name}</p>
                            <p className="text-xs text-slate-500">{payment.amount}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          payment.status === "Completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600">
              Start free, upgrade when you need more features
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Free Tier */}
            <Card className="relative border-slate-200">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-slate-900">Free</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$0</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {["Multi-currency wallet", "3 FX conversions/month", "Basic savings vaults", "Mobile money support"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full mt-8 rounded-full" variant="outline" data-testid="pricing-free-btn">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Consumer Plus */}
            <Card className="relative border-[#14B8A6] border-2 shadow-lg">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#14B8A6] text-white text-sm font-medium rounded-full">
                Popular
              </div>
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-slate-900">Consumer Plus</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$2.99</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {["Unlimited FX conversions", "Liquidity advances up to $50", "All vault types", "Lower spreads (0.8%)", "Priority support"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full mt-8 rounded-full bg-[#14B8A6] hover:bg-[#0D9488]" data-testid="pricing-plus-btn">
                    Start Free Trial
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* SME Pro */}
            <Card className="relative border-slate-200">
              <CardContent className="p-8">
                <h3 className="text-xl font-semibold text-slate-900">SME Pro</h3>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$9.99</span>
                  <span className="text-slate-600">/month</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {["Everything in Plus", "Working capital up to $500", "Supplier payments", "Transaction reports", "API access", "Lowest spreads (0.5%)"].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <svg className="h-5 w-5 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <Link to="/register">
                  <Button className="w-full mt-8 rounded-full" variant="outline" data-testid="pricing-sme-btn">
                    Contact Sales
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#14B8A6]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Move Money Across Africa?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Join thousands of users already sending money with OnePay
          </p>
          <Link to="/register">
            <Button size="lg" className="rounded-full bg-white text-[#14B8A6] hover:bg-slate-100 text-base px-8 h-12" data-testid="cta-get-started-btn">
              Get Started Free <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand Column */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#14B8A6] to-[#0D9488] flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-sm tracking-tight">OP</span>
                </div>
                <span className="text-xl font-bold text-white">One<span className="text-[#14B8A6]">Pay</span></span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Moving money across Africa, instantly. The trusted platform for cross-border payments.
              </p>
              <div className="flex items-center gap-3 mt-6">
                {["🇰🇪", "🇺🇬", "🇹🇿", "🇷🇼"].map((flag, i) => (
                  <span key={i} className="text-xl opacity-60 hover:opacity-100 transition-opacity">{flag}</span>
                ))}
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#business" className="hover:text-white transition-colors">For Business</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Press</a></li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">© 2025 OnePay. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
              </a>
              <a href="#" className="text-slate-400 hover:text-white transition-colors">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
